// assetService.js - API client for Assets feature
// Handles all asset-related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * List all assets for the authenticated user
 * @returns {Promise<object>} - { success, assets, count, limits }
 */
export async function getAssets() {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-list`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to list assets (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error listing assets:', error);
        throw error;
    }
}

/**
 * Create a new monitored asset
 * @param {string} assetType - 'domain', 'ip', or 'cidr'
 * @param {string} value - The asset value (e.g., 'example.com')
 * @returns {Promise<object>} - { success, assetId, asset, message, limits }
 */
export async function createAsset(assetType, value) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-create`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ assetType, value }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            // Include additional error info for limit errors
            const error = new Error(data.error || `Failed to create asset (${response.status})`);
            error.statusCode = response.status;
            error.limit = data.limit;
            error.current = data.current;
            error.tier = data.tier;
            error.upgradeMessage = data.upgradeMessage;
            error.upgradeUrl = data.upgradeUrl;
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error creating asset:', error);
        throw error;
    }
}

/**
 * Get details for a specific asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - { success, asset, recentJobs }
 */
export async function getAsset(assetId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-get/${assetId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get asset (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting asset:', error);
        throw error;
    }
}

/**
 * Delete (soft-delete) an asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - { success, message, assetId, deletedAt }
 */
export async function deleteAsset(assetId) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 480000); // 8-minute timeout (function timeout is 10m)

    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-delete/${assetId}`, {
            method: 'DELETE',
            headers,
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to delete asset (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        clearTimeout(timeoutId);
        console.error('Error deleting asset:', error);
        throw error;
    }
}

/**
 * Trigger an immediate scan for an asset
 * @param {string} assetId - The asset ID
 * @param {string} scanType - 'discovery', 'vuln-scan', or legacy 'baseline'/'delta'
 * @returns {Promise<object>} - { success, scanType, scheduledAt, message }
 */
export async function triggerAssetScan(assetId, scanType = 'discovery') {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-scan-trigger`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ assetId, scanType }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to trigger scan (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error triggering asset scan:', error);
        throw error;
    }
}

/**
 * Get scan results for an asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - Asset scan results
 */
export async function getAssetResults(assetId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-result/${assetId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null; // No results yet
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get results (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting asset results:', error);
        throw error;
    }
}

/**
 * Get historical data for an asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - Historical snapshots
 */
export async function getAssetHistory(assetId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-history/${assetId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get history (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting asset history:', error);
        throw error;
    }
}

/**
 * Get findings for an asset with optional filters
 * @param {string} assetId - The asset ID
 * @param {object} filters - Optional filters { severity, status, search }
 * @returns {Promise<object>} - { success, findings, count }
 */
export async function getAssetFindings(assetId, filters = {}) {
    try {
        const headers = await getHeaders();
        const params = new URLSearchParams(filters);

        const response = await fetch(
            `${API_BASE_URL}/asset-findings/${assetId}?${params}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get findings (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting asset findings:', error);
        throw error;
    }
}

/**
 * Reprocess asset metrics from stored blob data
 * Useful when metrics weren't extracted properly during initial scan
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - { success, metrics, message }
 */
export async function reprocessAsset(assetId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-reprocess/${assetId}`, {
            method: 'POST',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to reprocess asset (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error reprocessing asset:', error);
        throw error;
    }
}

/**
 * Reset asset to fresh state - clears all scan data and jobs
 * Useful when scans get stuck or you want to start fresh
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - { success, deleted, message }
 */
export async function resetAsset(assetId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/asset-reset/${assetId}`, {
            method: 'POST',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to reset asset (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error resetting asset:', error);
        throw error;
    }
}

/**
 * Get full scan details including stage results for tool summary cards
 * @param {string} assetId - The asset ID
 * @param {string} resultId - The result ID
 * @returns {Promise<object>} - Full scan result data with stageResults
 */
export async function getAssetScanDetails(assetId, resultId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(
            `${API_BASE_URL}/asset-scan-details/${assetId}/${resultId}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get scan details (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting scan details:', error);
        throw error;
    }
}

/**
 * Get download URL for raw scan data zip
 * @param {string} assetId - The asset ID
 * @param {string} resultId - The result ID
 * @returns {Promise<object>} - { success, url, expiresAt, filename, availableFiles }
 */
export async function getAssetRawDataUrl(assetId, resultId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(
            `${API_BASE_URL}/asset-rawdata-download/${assetId}/${resultId}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get download URL (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting raw data URL:', error);
        throw error;
    }
}

/**
 * Download a specific file from the raw scan data
 * Extracts the file from the ZIP and triggers browser download
 * @param {string} assetId - The asset ID
 * @param {string} resultId - The result ID
 * @param {string} fileName - The file name to download (e.g., 'live_subdomains.txt')
 * @returns {Promise<void>}
 */
export async function downloadAssetFile(assetId, resultId, fileName) {
    try {
        const headers = await getHeaders();
        // Remove Content-Type for blob response
        delete headers['Content-Type'];

        const response = await fetch(
            `${API_BASE_URL}/asset-rawdata-download/${assetId}/${resultId}?file=${encodeURIComponent(fileName)}`,
            {
                method: 'GET',
                headers,
            }
        );

        if (!response.ok) {
            // Try to parse as JSON error
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to download file (${response.status})`);
            }
            throw new Error(`Failed to download file (${response.status})`);
        }

        // Get the file content as blob
        const blob = await response.blob();

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading asset file:', error);
        throw error;
    }
}

/**
 * Update asset scan frequencies
 * @param {string} assetId - The asset ID
 * @param {object} frequencies - { scanFrequency?, deltaFrequency? }
 * @returns {Promise<object>} - { success, assetId, updates }
 */
export async function updateAssetFrequency(assetId, frequencies) {
    try {
        const headers = await getHeaders();

        const response = await fetch(
            `${API_BASE_URL}/asset-update-frequency/${assetId}`,
            {
                method: 'PATCH',
                headers,
                body: JSON.stringify(frequencies),
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to update frequency (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating asset frequency:', error);
        throw error;
    }
}

// Default export for convenience
/**
 * Request a full rescan cycle for an asset
 * Full cycle: Discovery → Vuln Scan → Deep Security → Claude Enrichment
 * Rate limited to 1 per asset per 24 hours
 * @param {string} assetId - The asset ID
 * @param {string} reason - Optional reason for the rescan
 * @returns {Promise<object>} - { success, message, requestedAt, stages }
 */
export async function requestFullRescan(assetId, reason = '') {
    try {
        const headers = await getHeaders();

        const response = await fetch(
            `${API_BASE_URL}/asset-full-rescan/${assetId}`,
            {
                method: 'POST',
                headers,
                body: JSON.stringify({ reason }),
            }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            const error = new Error(data.error || `Failed to request rescan (${response.status})`);
            error.statusCode = response.status;
            error.message = data.message || data.error;
            error.nextAvailableAt = data.nextAvailableAt;
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error requesting full rescan:', error);
        throw error;
    }
}

const assetService = {
    getAssets,
    createAsset,
    getAsset,
    deleteAsset,
    triggerAssetScan,
    getAssetResults,
    getAssetHistory,
    getAssetFindings,
    reprocessAsset,
    resetAsset,
    getAssetScanDetails,
    getAssetRawDataUrl,
    downloadAssetFile,
    updateAssetFrequency,
    requestFullRescan,
    getAssetScoreBreakdown,
    getRiskTimeline,
    getAssetScoreExplain,
};

/**
 * PRD-51: Get score breakdown for an asset
 */
export async function getAssetScoreBreakdown(assetId) {
    try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE_URL}/asset-score-breakdown/${assetId}`, {
            method: 'GET',
            headers,
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get score breakdown (${response.status})`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error getting score breakdown:', error);
        throw error;
    }
}

/**
 * PRD-59 Tier 2: Get the cumulative risk timeline for an asset.
 * Returns rows sorted ascending by `ts` (oldest first); each row carries
 * score, level, vuln severity counts, eventType, and trigger metadata.
 * Backend is cached 60s and 404s if the asset doesn't belong to the caller.
 * @param {string} assetId - The asset ID
 * @param {{ since?: string }} [options] - Optional ISO timestamp lower bound
 * @returns {Promise<object>} - { success, assetId, timeline, cumulativeStateHash, count }
 */
export async function getRiskTimeline(assetId, { since } = {}) {
    try {
        const headers = await getHeaders();
        const url = new URL(`${API_BASE_URL}/asset-risk-timeline/${encodeURIComponent(assetId)}`);
        if (since) url.searchParams.set('since', since);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get risk timeline (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting risk timeline:', error);
        throw error;
    }
}

/**
 * PRD-133: Change-attribution for a single risk-trend point. Explains how the score moved from
 * the previous timeline point to `at` — Δscore, findings added/resolved by severity, the trigger,
 * and a Claude-reassessment / degraded-AI-window callout (server-side enrichment join).
 * Backend is cached 60s and 404s if the asset doesn't belong to the caller.
 * @param {string} assetId - The asset ID
 * @param {string} at - ISO timestamp of the clicked point (omit → latest point)
 * @returns {Promise<object>} - { success, assetId, at, from, to, deltaScore, findingsDelta, trigger, reassessment }
 */
export async function getAssetScoreExplain(assetId, at) {
    try {
        const headers = await getHeaders();
        const url = new URL(`${API_BASE_URL}/asset-score-explain/${encodeURIComponent(assetId)}`);
        if (at) url.searchParams.set('at', at);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get score explanation (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting score explanation:', error);
        throw error;
    }
}

export default assetService;
